import React, { useEffect, useRef, useState } from "react";
import { Menu, X, Instagram, Linkedin } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { logoUrl, socials } from "../mock";

const SECTIONS = [
  { label: "Accueil", path: "/" },
  { label: "Réalisations", path: "/realisations" },
  { label: "Rachat de propriété", path: "/rachat-de-propriete" },
  { label: "À propos", path: "/a-propos" },
  { label: "Contact", path: "/contact" },
];

const TopBar = () => (
  <div className="hidden md:flex items-center justify-end gap-8 px-10 py-2.5 bg-neutral-950 text-white/85 text-xs tracking-wide">
    <a href={socials.linkedin} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-white transition-colors">
      <Linkedin className="w-3.5 h-3.5" strokeWidth={1.8} /> LinkedIn
    </a>
    <a href={socials.instagram} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-white transition-colors">
      <Instagram className="w-3.5 h-3.5" strokeWidth={1.8} /> Instagram
    </a>
  </div>
);

const Navbar = () => {
  const [onDarkBg, setOnDarkBg] = useState(true);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const lightHeader = !onDarkBg;
  const navWrapRef = useRef(null);

  useEffect(() => {
    const HEADER_PROBE_Y = 60; // point just below the fixed header/topbar

    const detectTheme = () => {
      const wrap = navWrapRef.current;
      // Temporarily ignore the fixed header itself so we can see what's
      // actually behind it at that point on the page.
      if (wrap) wrap.style.pointerEvents = "none";
      const el = document.elementFromPoint(window.innerWidth / 2, HEADER_PROBE_Y);
      if (wrap) wrap.style.pointerEvents = "";
      const themed = el && el.closest("[data-nav-theme]");
      const theme = themed ? themed.getAttribute("data-nav-theme") : "light";
      setOnDarkBg(theme === "dark");
    };

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        detectTheme();
        ticking = false;
      });
    };

    detectTheme();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  const goTo = (path) => {
    setOpen(false);
    navigate(path);
  };

  return (
    <>
      <div ref={navWrapRef} className="fixed top-0 left-0 right-0 z-50">
        <TopBar />
        <header className="transition-colors duration-300 bg-transparent">
          <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-3 md:py-4 flex items-center justify-between">
            {/* Logo left */}
            <button
              onClick={() => goTo("/")}
              className="flex items-center"
              aria-label="Maison Labroca - Accueil"
            >
              <img
                src={logoUrl}
                alt="Maison Labroca Construction"
                className="h-20 md:h-24 w-auto object-contain origin-left"
                style={
                  lightHeader
                    ? { imageRendering: "-webkit-optimize-contrast" }
                    : {
                        filter: "brightness(0) invert(1)",
                        imageRendering: "-webkit-optimize-contrast",
                      }
                }
              />
            </button>

            {/* Right: hamburger with label for clarity */}
            <button
              onClick={() => setOpen(true)}
              aria-label="Ouvrir le menu"
              className={`flex items-center gap-3 pl-5 pr-2 md:pr-3 py-2 rounded-full transition-colors ${
                lightHeader
                  ? "border border-neutral-300 text-neutral-900 hover:bg-neutral-100"
                  : "border border-white/60 text-white hover:bg-white/10"
              }`}
            >
              <span className="hidden sm:inline text-[13px] uppercase tracking-[0.15em] font-medium">
                Menu
              </span>
              <span className="w-11 h-11 rounded-full flex items-center justify-center">
                <Menu className="w-6 h-6" strokeWidth={1.8} />
              </span>
            </button>
          </div>
        </header>
      </div>

      {/* Slide-in menu panel */}
      <div
        className={`fixed inset-0 z-[70] transition-opacity duration-300 ${
          open ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"
        }`}
      >
        <div
          onClick={() => setOpen(false)}
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        />
        <aside
          className={`absolute top-0 right-0 h-full w-full sm:w-[460px] bg-white shadow-2xl transform transition-transform duration-500 ease-out overflow-y-auto ${
            open ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="flex items-center justify-between px-8 py-5 border-b border-neutral-100 sticky top-0 bg-white z-10">
            <img
              src={logoUrl}
              alt="Maison Labroca"
              className="h-10 w-auto object-contain"
            />
            <button
              onClick={() => setOpen(false)}
              aria-label="Fermer le menu"
              className="w-10 h-10 rounded-full border border-neutral-200 flex items-center justify-center hover:bg-neutral-100"
            >
              <X className="w-4 h-4 text-neutral-900" strokeWidth={1.8} />
            </button>
          </div>
          <nav className="flex flex-col px-6 py-2">
            {SECTIONS.map((item, idx) => (
              <button
                key={item.path}
                onClick={() => goTo(item.path)}
                className="group text-left px-2 py-3.5 border-b border-neutral-100 flex items-baseline justify-between hover:pl-4 transition-all"
              >
                <span className="font-display uppercase text-neutral-900 text-[22px] md:text-[26px] leading-none group-hover:text-[#9F8B76] transition-colors">
                  {item.label}
                </span>
                <span className="text-neutral-400 text-[11px] tabular-nums">
                  0{idx + 1}
                </span>
              </button>
            ))}
          </nav>
          <div className="px-8 py-6">
            <div className="flex items-center justify-center gap-6 text-neutral-500 text-xs tracking-wide">
              <a href={socials.linkedin} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-neutral-900">
                <Linkedin className="w-3.5 h-3.5" /> LinkedIn
              </a>
              <a href={socials.instagram} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-neutral-900">
                <Instagram className="w-3.5 h-3.5" /> Instagram
              </a>
            </div>
          </div>
        </aside>
      </div>
    </>
  );
};

export default Navbar;

